/**
 * Mantém o canvas do Phaser (Scale.FIT) do tamanho real da janela.
 *
 * O ScaleManager só recalcula em `window.resize` e `orientationchange` e não observa o elemento
 * pai. No WebView do Android (barra de tarefas e janelas do tablet, volta do segundo plano) a
 * janela pode ganhar o tamanho final sem esse evento chegar na hora: o canvas fica do tamanho
 * antigo, centralizado e cercado de faixas pretas.
 */

/** Atrasos (ms) dos recálculos de reforço depois do boot, para o dimensionamento tardio da janela. */
const REFORCOS_APOS_O_BOOT = [0, 300, 1000, 3000];
/** Eventos em sequência (ex.: resize + observador do pai) viram um recálculo só. */
const JANELA_DE_COALESCENCIA = 16;

/**
 * @param {{ scale: { refresh: () => void, parent?: Element | null } }} jogo instância do Phaser
 * @param {object} [opcoes]
 * @param {Element | null} [opcoes.pai] elemento observado (padrão: o pai do canvas no Phaser)
 * @param {number[]} [opcoes.reforcos] atrasos dos recálculos de reforço
 * @returns {() => void} desliga tudo
 */
export function ligarAjusteDeTela (jogo, { pai = jogo.scale.parent, reforcos = REFORCOS_APOS_O_BOOT } = {}) {
    const timers = new Set();
    let pendente = null;

    const recalcular = () => {
        pendente = null;
        jogo.scale.refresh();
    };
    const agendar = () => {
        if (pendente !== null) return;
        pendente = setTimeout(recalcular, JANELA_DE_COALESCENCIA);
    };
    const aoVoltarVisivel = () => {
        if (document.visibilityState === 'visible') agendar();
    };

    const naJanela = ['resize', 'orientationchange', 'pageshow', 'focus'];
    naJanela.forEach((evento) => window.addEventListener(evento, agendar));
    document.addEventListener('visibilitychange', aoVoltarVisivel);
    window.visualViewport?.addEventListener('resize', agendar);

    const observador = typeof ResizeObserver === 'function' && pai ? new ResizeObserver(agendar) : null;
    observador?.observe(pai);

    reforcos.forEach((atraso) => timers.add(setTimeout(agendar, atraso)));

    return () => {
        naJanela.forEach((evento) => window.removeEventListener(evento, agendar));
        document.removeEventListener('visibilitychange', aoVoltarVisivel);
        window.visualViewport?.removeEventListener('resize', agendar);
        observador?.disconnect();
        timers.forEach(clearTimeout);
        if (pendente !== null) clearTimeout(pendente);
        pendente = null;
    };
}
